import {Injectable} from '@angular/core';
import {Location} from '@angular/common';
import {ActivatedRouteSnapshot, CanActivate, NavigationEnd, Router, RouterStateSnapshot} from '@angular/router';
import {Observable} from 'rxjs';
import {Store} from "@ngrx/store";
import * as fromStore from "../../store";
import {filter, first, switchMap} from "rxjs/operators";
import {combineLatest} from 'rxjs';
import {UserSubscription, UserSubscriptionType} from "../../user/user-subscription";
import {Website} from "./website";
import {
  BOOSTER_CONNECTED,
  HOSTED,
  NETWORK_CHILD_SUB_DOMAIN,
  NETWORK_CHILD_SUB_FOLDER,
  NETWORK_PARENT_SUB_DOMAIN,
  NETWORK_PARENT_SUB_FOLDER,
  ONE_PRODUCT_BUNDLE_CONNECTED,
  ONE_PRODUCT_BUNDLE_CONNECTED_WITH_BOOSTER,
  TRIAL_HOSTED
} from "./website-access-permissions";

@Injectable()
export class WebsiteGuard implements CanActivate {

  constructor(
    private _location: Location,
    private _router: Router,
    private _store: Store<fromStore.DbStoreState>
  ) {
  }

  canActivate(next: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> | Promise<boolean> | boolean {
    return new Promise<boolean>((resolve: Function, reject: Function): void => {
      const websiteId: number = +next.params['id'];
      this._store.dispatch(new fromStore.LoadWebsiteProducts(websiteId));
      this._store.select(fromStore.getWebsitesLoaded)
        .pipe(
          filter((loaded: boolean): boolean => loaded),
          switchMap(() => combineLatest(
            this._store.select(fromStore.getUserSubscription),
            this._store.select(fromStore.getWebsite(websiteId)),
            this._store.select(fromStore.getWebsiteOptimizerPlugin(websiteId))
          )),
          first(([userSubscription]) => !!userSubscription),
        )
        .subscribe(([userSubscription, website, boosterPlugin]) => {
          if (website && website.isAiBuilderDemo && window.innerWidth < 991) {
              this._router.navigate(['/single-page-demo'], {queryParams: next.queryParams});
              return resolve(false);
          }
          else {
            if ((website && website.isActive) || (website && !website.isActive && website.cloudflareEnabled)) {
              const boosterIsActive: boolean = !website.domain_hash || (!!website.domain_hash && !!boosterPlugin && !!boosterPlugin.active);
              const cdnIsActive = website && website.cloudflareEnabled;
              this.allowRouteAccess(userSubscription, website, state.url, boosterIsActive, cdnIsActive) ? resolve(true) : reject();
            } else {
              this.navigateTo404(state.url);
              return reject();
            }
          }
        });
    });
  }

  private navigateTo404(url): void {
    const fromBanner = window.location.href.indexOf("fromBanner") > -1;
    if (fromBanner) {
      this._router.navigate(['/websites']);
    } else {
      this._router.navigate(['/404'], {skipLocationChange: true, replaceUrl: true});
      this._router.events
      .pipe(filter((event: any) => event instanceof NavigationEnd),
        first())
        .subscribe(() => {
          this._location.replaceState(url);
        });
    }
  }

  private allowRouteAccess(
    userSubscription: UserSubscription,
    website: Website,
    url: string,
    boosterPluginActive: boolean,
    cdnIsActive: boolean,
  ): boolean {
    /*TODO HOVO connectedFrom condition*/
    if ([UserSubscriptionType.FREE, UserSubscriptionType.PLATFORM].includes(userSubscription.type) && (!website.domain_hash && website.connectedFrom !== 'speed_optimizer')) {
      this._router.navigate(['/websites']);
      return false;
    }
    if (website && !website.domain_hash && website.connectedFrom && website.connectedFrom === 'speed_optimizer' && userSubscription && userSubscription.trialClient) {
      this._router.navigate(['/websites']);
      return false;
    }

    const baseUrl: string = url.split('?')[0].replace(/(\/websites\/\d+\/)|(\/\d+)|(\/$)/g, '');
    const permissions: string[] = this.getPermissions(userSubscription, website, boosterPluginActive, cdnIsActive);

    if (!permissions.length || permissions.includes(baseUrl)) {
      return true;
    } else {
      this._router.navigate(['/websites/' + website.id.toString() + '/' + permissions[0]]);
      return false;
    }
  }

  private getPermissions(userSubscription: UserSubscription, website: Website, boosterPluginActive: boolean, cdnIsActive: boolean): string[] {

    if (website.isActive && !website.isLocalhost && !website.loggedOut && !website.domain_hash
      && [UserSubscriptionType.ONE_PRODUCT, UserSubscriptionType.PREMIUM].includes(userSubscription.type)) {
      if (userSubscription.plan.services.speed_optimizer) {
        return ONE_PRODUCT_BUNDLE_CONNECTED_WITH_BOOSTER;
      }
      else {
        return ONE_PRODUCT_BUNDLE_CONNECTED;
      }
    }
    if ((!website.domain_hash && userSubscription && (userSubscription.trialBoosterClient
        || userSubscription.type === UserSubscriptionType.PLATFORM || userSubscription.type === UserSubscriptionType.PREMIUM
        || userSubscription.type === UserSubscriptionType.ONE_PRODUCT))
      || (website.cloudflareEnabled && userSubscription.boosterProClient)) {

      if (website.domain_hash) {
       return HOSTED;
      }

      let _permissions = BOOSTER_CONNECTED;
      if (!boosterPluginActive) {
        _permissions = _permissions.filter(route => !route.includes('frontend') && !route.includes('hosting-performance') && !route.includes('pro'));
      }
      return _permissions;
    }
    if (userSubscription.type === UserSubscriptionType.FREE) {
      return TRIAL_HOSTED;
    }

    let permissions: string[];
    if (website.isNetwork) {
      if (!website.parentId) {
        permissions = website.multisiteType === 'subdomain' ? NETWORK_PARENT_SUB_DOMAIN : NETWORK_PARENT_SUB_FOLDER;
      } else {
        permissions = website.multisiteType === 'subdomain' ? NETWORK_CHILD_SUB_DOMAIN : NETWORK_CHILD_SUB_FOLDER;
      }
    } else {
      permissions = HOSTED;
    }
    if (!website.domain_hash) {
      permissions = permissions.filter(route => !route.includes('hosting/'));
    }

    return permissions;
  }
}
